import { useEffect, useState } from 'react';
import { CircularProgress, type SvgIconProps } from '@mui/material';

import AttentionBanner from 'modules/common/components/AttentionBanner';
import { StatementFile } from 'modules/common/components/VerifiedStatementUpload/components/StatementSlotList';
import useAwaitingStatements from 'modules/common/components/VerifiedStatementUpload/hooks/useAwaitingStatements';

/**
 * The banner's spinner, shaped to fit `AttentionBanner`'s icon slot.
 *
 * That slot is typed to take an icon component so the banner can hand its variant colour down;
 * this accepts the same props and forwards them to the spinner the rest of the app already uses.
 * `color="inherit"` is what lets the passed-in colour win over `CircularProgress`'s themed default.
 *
 * The design draws a static arc rotating for one second of a two-second loop and holding for the
 * rest. That pause is not reproduced: a spinner that stops reads as stuck, and continuous is what
 * every other loading state here does.
 */
function ProcessingSpinner({ sx }: SvgIconProps) {
    return <CircularProgress size={16} color="inherit" sx={sx} />;
}

const TITLE = 'Processing your statements';
const DESCRIPTION = "We're working on it and will have them ready for you shortly.";

/**
 * Confirms a finished run, in the words of the design's success alert.
 *
 * "Documents" rather than "statements" because it counts what the member handed over, not what
 * came back: a run can finish with some of its files declined, and each of those states its own
 * reason on its own row.
 */
const uploadedMessage = (count: number) => `${count} ${count === 1 ? 'document' : 'documents'} uploaded successfully!`;

/**
 * A finished run where some documents could not be read. Names the way forward the cards don't:
 * choosing the month on the file files it whether or not we could read it, and re-uploading the
 * same file would only fail again.
 */
const UNREADABLE_TITLE = "We couldn't read some of your documents";
const unreadableMessage = (unreadable: number, total: number) =>
    `${unreadable} of ${total} ${
        total === 1 ? 'document' : 'documents'
    } couldn't be processed. Open the account, then use Choose month on ${
        unreadable === 1 ? 'the file' : 'each file'
    } to tell us which statement it is, or upload a clearer copy.`;

/**
 * How long the confirmation holds before it takes itself away.
 *
 * Longer than the app's success snackbar, because this is read in place rather than thrust in
 * front of the member: it has to survive long enough to be noticed by someone whose eyes are on
 * the cards below it.
 */
const CONFIRMATION_VISIBLE_MS = 5_000;

const BANNER_SX = {
    px: 2,
    py: 1,
    gap: 1.5,
    mb: 2,
    // `AttentionBanner` pins its dismiss to the top with a negative offset, which is right for a
    // banner whose description runs to several lines. This matches the Figma design.
    '& .MuiIconButton-root': { alignSelf: 'center', mt: 0 },
};

interface StatementProcessingBannerProps {
    /** Every statement the member has uploaded, across all accounts. */
    readonly files: readonly StatementFile[];
}

/**
 * Speaks for a run of statements after the member has closed the upload modal — first that they
 * are being read, then that they are done.
 *
 * The dropzone's own processing screen covers the modal-open case and disappears with it. Without
 * this there is nothing at all behind it: the cards go back to reading as incomplete while the
 * server is still working, and a member has no way to tell "we have not started" from "we are
 * nearly done".
 *
 * Both halves live here rather than the confirmation going to a snackbar, so one surface carries
 * one story. A banner that has spent two minutes saying "Processing your statements" turning
 * green and saying they are in is a sentence the member has already been reading; a snackbar
 * arriving elsewhere is a second thing to notice and to connect back to this one.
 *
 * Renders nothing when there is nothing to say, so callers can mount it unconditionally.
 */
export default function StatementProcessingBanner({ files }: Readonly<StatementProcessingBannerProps>) {
    const { awaitingFiles, run } = useAwaitingStatements(files);

    /**
     * The run this member dismissed, or `null` for "none yet".
     *
     * Keyed on the run rather than held as a plain boolean because the alternative is a member
     * who dismisses once and is never told about an upload again — the failure this banner exists
     * to prevent, reintroduced by its own close button.
     *
     * A dismissal covers the whole run, confirmation included. Dismissing mid-processing says
     * "stop telling me about this drop", and coming back a minute later to announce the same drop
     * finished would be overriding that.
     */
    const [dismissedRunId, setDismissedRunId] = useState<number | null>(null);

    const runId = run?.id;
    const isDismissed = runId !== undefined && dismissedRunId === runId;
    const isProcessing = awaitingFiles.length > 0;
    const isConfirming = run?.isFinished === true;
    const unreadableCount = run?.unreadableCount ?? 0;

    // Retiring the confirmation is the same act as dismissing it, so it goes through the same
    // state: a run that has had its say is a run this banner is finished with.
    useEffect(() => {
        // A run with unreadable files stays up until dismissed: it asks the member to act.
        if (!isConfirming || isDismissed || runId === undefined || unreadableCount > 0) {
            return undefined;
        }
        const timer = setTimeout(() => setDismissedRunId(runId), CONFIRMATION_VISIBLE_MS);
        return () => clearTimeout(timer);
    }, [isConfirming, isDismissed, runId, unreadableCount]);

    if (isDismissed || (!isProcessing && !isConfirming)) {
        return null;
    }

    const dismiss = () => setDismissedRunId(runId ?? null);

    // Processing wins over a finished run, so a second drop landing on the confirmation of the
    // first is narrated rather than sitting under "uploaded successfully".
    if (isProcessing) {
        return (
            <AttentionBanner
                title={TITLE}
                description={DESCRIPTION}
                variant="info-important"
                icon={ProcessingSpinner}
                onDismiss={dismiss}
                sx={BANNER_SX}
                slotProps={{ title: { sx: { mb: 0 } } }}
            />
        );
    }

    if (unreadableCount > 0) {
        return (
            <AttentionBanner
                title={UNREADABLE_TITLE}
                description={unreadableMessage(unreadableCount, run?.count ?? 0)}
                variant="warning"
                onDismiss={dismiss}
                sx={BANNER_SX}
            />
        );
    }

    return (
        <AttentionBanner
            description={uploadedMessage(run?.count ?? 0)}
            variant="success"
            onDismiss={dismiss}
            sx={BANNER_SX}
        />
    );
}
